import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCog,
  FaBox,
  FaFolder,
  FaFolderOpen,
  FaSave,
  FaTimes,
  FaChevronRight,
  FaChevronDown,
  FaFile,
} from "react-icons/fa";
import CategoryTreeSelector from "./components/CategoryTreeSelector";

const CategoryManagement = () => {
  const [assetClass, setAssetClass] = useState("fixed");
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parent_id: "",
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    fetchCategories();
    // Reset form data when asset class changes
    setFormData({ name: "", description: "", parent_id: "" });
    setEditingCategory(null);
    setShowModal(false);
  }, [assetClass]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/categories?&asset_class=${assetClass}`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setCategories(data.categories || []);
      
      // Auto-expand all parent categories for better initial view
      // const expandAllParents = (cats, expanded = new Set()) => {
      //   cats.forEach(cat => {
      //     if (cat.children && cat.children.length > 0) {
      //       expanded.add(cat.id);
      //       expandAllParents(cat.children, expanded);
      //     }
      //   });
      //   return expanded;
      // };
      // setExpandedNodes(expandAllParents(data.categories || []));
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      const url = editingCategory
        ? `${process.env.REACT_APP_BASE_URL}/api/assets/categories/${editingCategory.id}`
        : `${process.env.REACT_APP_BASE_URL}/api/assets/categories`;
      const method = editingCategory ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          asset_class: assetClass,
          parent_id: formData.parent_id || null,
        }),
      });

      if (response.ok) {
        alert(
          `Category ${editingCategory ? "updated" : "created"} successfully!`
        );
        setFormData({ name: "", description: "", parent_id: "" });
        setEditingCategory(null);
        setShowModal(false);
        fetchCategories();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error saving category:", error);
      alert("Error saving category");
    }
    setIsLoading(false);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      parent_id: category.parent_id || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/categories/${categoryId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert("Category deleted successfully!");
        fetchCategories();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      alert("Error deleting category");
    }
  };

  const openAddModal = (parentId = null) => {
    setFormData({
      name: "",
      description: "",
      parent_id: parentId || "",
    });
    setEditingCategory(null);
    setShowModal(true);
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderCategoryTree = (cats, level = 0) => {
    return cats.map((category) => {
      const hasChildren = category.children && category.children.length > 0;
      const isExpanded = expandedNodes.has(category.id);

      return (
        <div key={category.id} className="select-none">
          {/* Node Row */}
          <div className="flex items-center px-2 py-3 hover:bg-slate-50 rounded-lg transition-colors border border-slate-200 bg-white/60 shadow-sm mb-2">
            {/* Indentation Spacer */}
            <div style={{ width: `${level * 24}px` }} className="flex-shrink-0" />
            
            {/* Expand/Collapse Icon */}
            <div className="w-4 h-4 flex items-center justify-center mr-3 flex-shrink-0">
              {hasChildren ? (
                <button
                  onClick={() => toggleNode(category.id)}
                  className="hover:bg-slate-200 rounded p-1 transition-colors"
                >
                  {isExpanded ? (
                    <FaChevronDown className="w-3 h-3 text-slate-400" />
                  ) : (
                    <FaChevronRight className="w-3 h-3 text-slate-400" />
                  )}
                </button>
              ) : (
                <div className="w-3 h-3" />
              )}
            </div>

            {/* Category Icon */}
            <div className="mr-3 flex-shrink-0">
              {category.isLeaf ? (
                <FaFile className="w-4 h-4 text-blue-500" />
              ) : (
                <FaFolder className="w-4 h-4 text-amber-500" />
              )}
            </div>

            {/* Category Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-slate-800 truncate">
                    {category.name}
                    {!category.isLeaf && (
                      <span className="text-xs text-slate-400 ml-1 font-normal">(Category)</span>
                    )}
                  </h4>
                  {category.description && (
                    <p className="text-sm text-slate-600 truncate">
                      {category.description}
                    </p>
                  )}
                  {hasChildren && (
                    <p className="text-xs text-slate-500">
                      {category.children.length} subcategories
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    onClick={() => openAddModal(category.id)}
                    title="Add Subcategory"
                  >
                    <FaPlus className="w-3 h-3" />
                  </button>
                  <button
                    className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                    onClick={() => handleEdit(category)}
                    title="Edit Category"
                  >
                    <FaEdit className="w-3 h-3" />
                  </button>
                  <button
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    onClick={() => handleDelete(category.id)}
                    title="Delete Category"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Children */}
          {hasChildren && isExpanded && (
            <div className="space-y-1">
              {renderCategoryTree(category.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const getFlatCategories = () => {
    const flattenCategories = (cats, result = []) => {
      cats.forEach((cat) => {
        result.push(cat);
        if (cat.children && cat.children.length > 0) {
          flattenCategories(cat.children, result);
        }
      });
      return result;
    };
    return flattenCategories(categories);
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          Category Management
        </h2>
        <p className="mt-3 text-lg text-slate-600 max-w-2xl mx-auto">
          Organize your assets into categories and subcategories for better
          management
        </p>
      </div>

      {/* Asset Class Selection */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg p-8">
        <h3 className="text-2xl font-bold text-slate-800 mb-6">
          Select Asset Class
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            className={`p-6 border-2 rounded-2xl transition-all duration-300 text-left ${
              assetClass === "fixed"
                ? "border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100/80 shadow-xl"
                : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
            }`}
            onClick={() => setAssetClass("fixed")}
          >
            <div className="flex items-center space-x-4">
              <div
                className={`p-3 rounded-xl ${
                  assetClass === "fixed"
                    ? "bg-gradient-to-br from-purple-500 to-purple-600"
                    : "bg-slate-100"
                }`}
              >
                <FaCog
                  className={`w-6 h-6 ${
                    assetClass === "fixed" ? "text-white" : "text-slate-400"
                  }`}
                />
              </div>
              <div>
                <h4
                  className={`text-lg font-bold ${
                    assetClass === "fixed" ? "text-slate-800" : "text-slate-700"
                  }`}
                >
                  Fixed Assets
                </h4>
                <p
                  className={`text-sm ${
                    assetClass === "fixed" ? "text-slate-600" : "text-slate-500"
                  }`}
                >
                  Manage fixed asset categories
                </p>
              </div>
            </div>
          </button>

          <button
            className={`p-6 border-2 rounded-2xl transition-all duration-300 text-left ${
              assetClass === "trading"
                ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100/80 shadow-xl"
                : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
            }`}
            onClick={() => setAssetClass("trading")}
          >
            <div className="flex items-center space-x-4">
              <div
                className={`p-3 rounded-xl ${
                  assetClass === "trading"
                    ? "bg-gradient-to-br from-emerald-500 to-emerald-600"
                    : "bg-slate-100"
                }`}
              >
                <FaBox
                  className={`w-6 h-6 ${
                    assetClass === "trading" ? "text-white" : "text-slate-400"
                  }`}
                />
              </div>
              <div>
                <h4
                  className={`text-lg font-bold ${
                    assetClass === "trading"
                      ? "text-slate-800"
                      : "text-slate-700"
                  }`}
                >
                  Trading Assets
                </h4>
                <p
                  className={`text-sm ${
                    assetClass === "trading"
                      ? "text-slate-600"
                      : "text-slate-500"
                  }`}
                >
                  Manage trading asset categories
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Categories Section */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg">
        <div className="p-8 border-b border-slate-200/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className={`p-3 rounded-xl bg-gradient-to-br ${
                  assetClass === "fixed"
                    ? "from-purple-500 to-purple-600"
                    : "from-emerald-500 to-emerald-600"
                }`}
              >
                {assetClass === "fixed" ? (
                  <FaCog className="w-6 h-6 text-white" />
                ) : (
                  <FaBox className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">
                  {assetClass.charAt(0).toUpperCase() + assetClass.slice(1)}{" "}
                  Asset Categories
                </h3>
                <p className="text-slate-600">
                  {categories.length}{" "}
                  {categories.length === 1 ? "category" : "categories"}{" "}
                  configured
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {categories.length > 0 && (
                <>
                  <button
                    className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors text-sm"
                    onClick={() => {
                      const allParents = new Set();
                      const findAllParents = (cats) => {
                        cats.forEach(cat => {
                          if (cat.children && cat.children.length > 0) {
                            allParents.add(cat.id);
                            findAllParents(cat.children);
                          }
                        });
                      };
                      findAllParents(categories);
                      setExpandedNodes(allParents);
                    }}
                  >
                    Expand All
                  </button>
                  <button
                    className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors text-sm"
                    onClick={() => setExpandedNodes(new Set())}
                  >
                    Collapse All
                  </button>
                </>
              )}
              <button
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
                onClick={() => openAddModal()}
              >
                <FaPlus className="w-4 h-4" />
                <span>Add Root Category</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-8">
          {isLoading ? (
            <div className="text-center py-8">Loading categories...</div>
          ) : categories.length === 0 ? (
            <div className="text-center py-16">
              <div className="p-6 bg-slate-100 rounded-full w-fit mx-auto mb-6">
                <FaBox className="w-12 h-12 text-slate-400" />
              </div>
              <h4 className="text-2xl font-bold text-slate-800 mb-4">
                No Categories Found
              </h4>
              <p className="text-slate-600 max-w-md mx-auto leading-relaxed mb-8">
                You haven't created any categories for {assetClass} assets yet.
              </p>
              <button
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
                onClick={() => openAddModal()}
              >
                Create Your First Category
              </button>
            </div>
          ) : (
            <div className="space-y-2">{renderCategoryTree(categories)}</div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingCategory ? "Edit Category" : "Add New Category"}
                </h3>
                <button
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({ name: "", description: "", parent_id: "" });
                  }}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter category name"
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Enter category description"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  rows="3"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Parent Category
                </label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, parent_id: "" }))}
                    className={`w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm font-medium text-left transition-colors ${
                      formData.parent_id === "" 
                        ? "bg-blue-50 border-blue-300 text-blue-800" 
                        : "bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    🏠 Root Category (No Parent)
                  </button>
                  
                  {categories.length > 0 && (
                    <CategoryTreeSelector
                      categories={categories.filter((cat) => cat.id !== editingCategory?.id)}
                      selectedId={formData.parent_id}
                      onSelect={(category) =>
                        setFormData((prev) => ({ ...prev, parent_id: category.id }))
                      }
                      placeholder="Or select existing category as parent"
                      label=""
                      required={false}
                    />
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Choose "Root Category" to create a top-level category, or select an existing category to create a subcategory.
                </p>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({ name: "", description: "", parent_id: "" });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50"
                >
                  <FaSave className="w-4 h-4" />
                  <span>{editingCategory ? "Update" : "Create"} Category</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
